import { partnershipKey } from './partnershipKey';

export const DEFAULT_MEXICANO_PAIRING = '13v24';
export const ALTERNATE_MEXICANO_PAIRING = '14v23';

const buildLastRoundSet = (lastRoundPartnerships = []) => new Set(
  (Array.isArray(lastRoundPartnerships) ? lastRoundPartnerships : []).map((pair) => partnershipKey(pair))
);

const getPairRepeatCost = (pair, pairPlayCounts = {}, lastRoundSet = new Set()) => {
  const key = partnershipKey(pair);
  const historicalRepeats = pairPlayCounts[key] || 0;
  const repeatedLastRoundPenalty = lastRoundSet.has(key) ? 1000 : 0;
  return repeatedLastRoundPenalty + (historicalRepeats * 10);
};

export const normalizeMexicanoPairing = (value) => (
  value === ALTERNATE_MEXICANO_PAIRING
    ? ALTERNATE_MEXICANO_PAIRING
    : DEFAULT_MEXICANO_PAIRING
);

export const createMexicanoTeams = (group, pairing = DEFAULT_MEXICANO_PAIRING) => {
  if (!Array.isArray(group) || group.length < 4) {
    return [];
  }

  const normalizedPairing = normalizeMexicanoPairing(pairing);

  if (normalizedPairing === ALTERNATE_MEXICANO_PAIRING) {
    return [
      [group[0], group[3]],
      [group[1], group[2]],
    ];
  }

  return [
    [group[0], group[2]],
    [group[1], group[3]],
  ];
};

export const createMixedMexicanoTeams = (
  rankedMales,
  rankedFemales,
  pairing = DEFAULT_MEXICANO_PAIRING
) => {
  if (!Array.isArray(rankedMales) || !Array.isArray(rankedFemales) || rankedMales.length < 2 || rankedFemales.length < 2) {
    return [];
  }

  const normalizedPairing = normalizeMexicanoPairing(pairing);

  if (normalizedPairing === ALTERNATE_MEXICANO_PAIRING) {
    return [
      [rankedMales[0], rankedFemales[1]],
      [rankedMales[1], rankedFemales[0]],
    ];
  }

  return [
    [rankedMales[0], rankedFemales[0]],
    [rankedMales[1], rankedFemales[1]],
  ];
};

const scoreStandardMexicanoRanking = (
  rankedPlayers,
  pairing,
  pairPlayCounts,
  lastRoundSet
) => {
  let totalCost = 0;

  for (let index = 0; index + 3 < rankedPlayers.length; index += 4) {
    const teams = createMexicanoTeams(rankedPlayers.slice(index, index + 4), pairing);
    for (const pair of teams) {
      totalCost += getPairRepeatCost(pair, pairPlayCounts, lastRoundSet);
    }
  }

  return totalCost;
};

const scoreMixedMexicanoRanking = (
  rankedMales,
  rankedFemales,
  pairing,
  pairPlayCounts,
  lastRoundSet
) => {
  let totalCost = 0;
  const totalCourts = Math.min(Math.floor(rankedMales.length / 2), Math.floor(rankedFemales.length / 2));

  for (let courtIndex = 0; courtIndex < totalCourts; courtIndex++) {
    const malePair = rankedMales.slice(courtIndex * 2, courtIndex * 2 + 2);
    const femalePair = rankedFemales.slice(courtIndex * 2, courtIndex * 2 + 2);
    const teams = createMixedMexicanoTeams(malePair, femalePair, pairing);
    for (const pair of teams) {
      totalCost += getPairRepeatCost(pair, pairPlayCounts, lastRoundSet);
    }
  }

  return totalCost;
};

const optimizeTiedBlockBySwaps = ({
  rankedPlayers,
  blockStart,
  blockEnd,
  scoreRanking,
}) => {
  let optimized = [...rankedPlayers];
  let bestCost = scoreRanking(optimized);
  let improved = true;

  while (improved) {
    improved = false;
    let nextBest = optimized;
    let nextBestCost = bestCost;

    for (let left = blockStart; left < blockEnd - 1; left++) {
      for (let right = left + 1; right < blockEnd; right++) {
        const candidate = [...optimized];
        [candidate[left], candidate[right]] = [candidate[right], candidate[left]];
        const candidateCost = scoreRanking(candidate);

        if (candidateCost < nextBestCost) {
          nextBest = candidate;
          nextBestCost = candidateCost;
          improved = true;
        }
      }
    }

    optimized = nextBest;
    bestCost = nextBestCost;
  }

  return optimized;
};

const reorderTiedPlayersByScore = ({
  rankedPlayers,
  getScore,
  scoreRanking,
}) => {
  let optimized = [...rankedPlayers];
  let index = 0;

  while (index < optimized.length) {
    const currentScore = getScore(optimized[index]);
    let blockEnd = index + 1;

    while (blockEnd < optimized.length && getScore(optimized[blockEnd]) === currentScore) {
      blockEnd += 1;
    }

    if (blockEnd - index > 1) {
      optimized = optimizeTiedBlockBySwaps({
        rankedPlayers: optimized,
        blockStart: index,
        blockEnd,
        scoreRanking,
      });
    }

    index = blockEnd;
  }

  return optimized;
};

export const reorderTiedMexicanoPlayers = ({
  rankedPlayers,
  scores = {},
  pairing = DEFAULT_MEXICANO_PAIRING,
  pairPlayCounts = {},
  lastRoundPartnerships = [],
}) => {
  const lastRoundSet = buildLastRoundSet(lastRoundPartnerships);

  return reorderTiedPlayersByScore({
    rankedPlayers,
    getScore: (player) => scores[player] || 0,
    scoreRanking: (candidateRanking) => scoreStandardMexicanoRanking(
      candidateRanking,
      pairing,
      pairPlayCounts,
      lastRoundSet
    ),
  });
};

export const reorderTiedMixedMexicanoPlayers = ({
  rankedMales,
  rankedFemales,
  scores = {},
  pairing = DEFAULT_MEXICANO_PAIRING,
  pairPlayCounts = {},
  lastRoundPartnerships = [],
  passes = 2,
}) => {
  const lastRoundSet = buildLastRoundSet(lastRoundPartnerships);
  let nextMales = [...rankedMales];
  let nextFemales = [...rankedFemales];

  for (let pass = 0; pass < passes; pass++) {
    const currentMales = nextMales;
    const currentFemales = nextFemales;
    const optimizedMales = reorderTiedPlayersByScore({
      rankedPlayers: currentMales,
      getScore: (player) => scores[player] || 0,
      scoreRanking: (candidateRanking) => scoreMixedMexicanoRanking(
        candidateRanking,
        currentFemales,
        pairing,
        pairPlayCounts,
        lastRoundSet
      ),
    });

    const optimizedFemales = reorderTiedPlayersByScore({
      rankedPlayers: currentFemales,
      getScore: (player) => scores[player] || 0,
      scoreRanking: (candidateRanking) => scoreMixedMexicanoRanking(
        optimizedMales,
        candidateRanking,
        pairing,
        pairPlayCounts,
        lastRoundSet
      ),
    });

    const arraysEqual = (first, second) => (
      first.length === second.length && first.every((player, index) => player === second[index])
    );
    const changed =
      !arraysEqual(optimizedMales, currentMales)
      || !arraysEqual(optimizedFemales, currentFemales);

    nextMales = optimizedMales;
    nextFemales = optimizedFemales;

    if (!changed) {
      break;
    }
  }

  return {
    rankedMales: nextMales,
    rankedFemales: nextFemales,
  };
};
